import { useState } from 'react';
import toast from 'react-hot-toast';
import { useSeedDataStatus, useGenerateSeedData, useDeleteSeedData } from '../../../api/admin';
import { ConfirmDialog } from '../shared/ConfirmDialog';

export function SeedDataManager() {
  const { data: status, isLoading } = useSeedDataStatus();
  const generateMutation = useGenerateSeedData();
  const deleteMutation = useDeleteSeedData();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const hasSeedData = status && status.users > 0;

  const handleGenerate = () => {
    generateMutation.mutate(undefined, {
      onSuccess: (data) => {
        toast.success(`Generated ${data.users} users, ${data.projects} projects, ${data.asks} asks, ${data.transactions} donations`);
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to generate seed data');
      },
    });
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('All demo data deleted');
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete seed data');
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-spi-charcoal mb-2">Demo Data Manager</h3>
        <p className="text-sm text-spi-slate">
          Generate realistic demo data to preview how the marketplace looks with active users, projects, and donations. All demo data is flagged and can be cleanly removed.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Users', count: status?.users ?? 0, color: 'text-blue-600' },
          { label: 'Projects', count: status?.projects ?? 0, color: 'text-spi-green' },
          { label: 'Asks', count: status?.asks ?? 0, color: 'text-spi-orange' },
          { label: 'Donations', count: status?.transactions ?? 0, color: 'text-purple-600' },
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-xl border border-spi-parchment p-4 text-center">
            <div className={`text-2xl font-bold ${item.color}`}>{item.count}</div>
            <div className="text-xs text-spi-slate mt-1">Demo {item.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleGenerate}
          disabled={!!hasSeedData || generateMutation.isPending}
          className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generateMutation.isPending ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Generating...
            </span>
          ) : (
            'Generate Demo Data'
          )}
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={!hasSeedData || deleteMutation.isPending}
          className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {deleteMutation.isPending ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Deleting...
            </span>
          ) : (
            'Delete Demo Data'
          )}
        </button>
      </div>

      {hasSeedData && (
        <p className="text-xs text-spi-slate">
          Demo accounts use the password <code className="bg-spi-seed px-1.5 py-0.5 rounded text-spi-charcoal">SeedDemo123!</code>
        </p>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Demo Data"
        message="This will permanently remove all demo users, their projects, asks, and donations. Real user data will not be affected."
        confirmLabel="Delete All Demo Data"
        danger
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
